import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthForm } from "@/shared/ui";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/shared/core";

export const AuthPage: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"login" | "reset-password">("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const {
    signIn,
    resetPassword,
    user,
    profile,
    loading: authLoading,
    loadingProfile,
    loadingAdmin,
    isInitializing,
    needsOnboarding,
    getUserType,
    isAdmin,
  } = useAuth();

  // Check URL params for mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get("mode");
    if (modeParam === "reset-password") {
      setMode("reset-password");
    }
  }, []);

  // Calculate redirect destination using useMemo
  const redirectDestination = useMemo(() => {
    // Wait for auth to finish initializing
    if (isInitializing || authLoading) return null;

    // Wait for profile and admin status to load
    if (user && (profile === null || loadingProfile || loadingAdmin)) {
      return null;
    }

    if (!user || !profile) return null;

    // Admins go directly to admin dashboard
    if (isAdmin) {
      const savedPath = sessionStorage.getItem("redirectAfterAuth");
      if (savedPath && savedPath.startsWith("/admin")) {
        sessionStorage.removeItem("redirectAfterAuth");
        return savedPath;
      }
      return "/admin/dashboard";
    }

    // Profile is loaded, check onboarding status for regular users
    if (needsOnboarding()) {
      const userType = getUserType();
      if (!userType) {
        return "/onboarding/select-type";
      }
      return `/onboarding/${userType}`;
    }

    // Redirect to saved path or user-specific dashboard
    const savedPath = sessionStorage.getItem("redirectAfterAuth");
    if (savedPath) {
      sessionStorage.removeItem("redirectAfterAuth");
      return savedPath;
    }

    const userType = getUserType();
    // Always redirect to user-specific dashboard if userType is available
    if (userType) {
      return `/dashboard/${userType}`;
    }

    // Fallback to generic dashboard
    return ROUTES.DASHBOARD;
  }, [
    user,
    profile,
    authLoading,
    loadingProfile,
    loadingAdmin,
    isInitializing,
    needsOnboarding,
    getUserType,
    isAdmin,
  ]);

  // Redirect if already logged in
  useEffect(() => {
    if (redirectDestination) {
      navigate(redirectDestination, { replace: true });
    }
  }, [redirectDestination, navigate]);

  const handleSubmit = async (data: { email: string; password: string }) => {
    setLoading(true);
    setError("");

    try {
      if (mode === "reset-password") {
        await resetPassword(data.email);
        toast.success(t("auth.resetPassword.success"));
        setMode("login");
        setError("");
      } else {
        await signIn(data.email, data.password);
        toast.success(t("auth.login.success"));
        // El useEffect que escucha cambios en 'user' manejará la redirección
        // Si necesita onboarding, OnboardingRoute lo redirigirá automáticamente
      }
    } catch (err) {
      let errorMessage = t("auth.errors.generic");

      if (
        err instanceof Error &&
        err.message?.includes("Invalid login credentials")
      ) {
        errorMessage = t("auth.errors.invalidCredentials");
      } else if (
        err instanceof Error &&
        err.message?.includes("Email not confirmed")
      ) {
        errorMessage = t("auth.errors.emailNotConfirmed");
      } else if (err instanceof Error && err.message?.includes("email")) {
        errorMessage = t("auth.errors.invalidEmail");
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = () => {
    setMode(mode === "login" ? "reset-password" : "login");
    setError("");
  };

  return (
    <div className="relative min-h-screen bg-gradient-hero flex items-center justify-center p-4 overflow-hidden">
      {/* Soft gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-tiber-500/20 blur-3xl" />
        <div className="absolute bottom-1/4 -left-32 w-80 h-80 rounded-full bg-tiber-400/15 blur-3xl" />
      </div>
      {/* Animated background lines */}
      <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
        {/* Horizontal lines */}
        <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/40 to-transparent animate-pulse" />
        <div
          className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/30 to-transparent animate-pulse"
          style={{ animationDelay: "0.8s" }}
        />
        <div
          className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/35 to-transparent animate-pulse"
          style={{ animationDelay: "1.6s" }}
        />
        <div
          className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "2.4s" }}
        />
        {/* Diagonal sliding lines */}
        <div className="absolute inset-0">
          <div
            className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/25 to-transparent rotate-12 origin-left"
            style={{ animation: "slide 18s linear infinite" }}
          />
          <div
            className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/30 to-transparent -rotate-12 origin-left"
            style={{
              animation: "slide-reverse 22s linear infinite",
              animationDelay: "3s",
            }}
          />
        </div>
        {/* Vertical lines */}
        <div
          className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "0.3s" }}
        />
        <div
          className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "1.2s" }}
        />
        <div
          className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "1.8s" }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <AuthForm
          mode={mode}
          onResetPassword={handleModeSwitch}
          onSubmit={handleSubmit}
          isLoading={loading}
          error={error}
        />
      </div>
    </div>
  );
};
